import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { createProductOrder } from '../../../controllers/paymentsController';
import { searchMembersWithSubMembers } from '../../../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../../../api/httpClientGroups';
import { ProductPaymentMethod } from '../productOrderLogoUtils';
import { getCurrencyDisplayLabel } from '../../../utils/currencyDisplay';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '5px' },
  '& .MuiInputBase-input': { fontSize: '14px' },
};

const CreateProductOrderDialog = ({ open, onClose, onCreated, products = [] }) => {
  const activeProducts = useMemo(
    () => products.filter((p) => p.isActive),
    [products],
  );

  const [form, setForm] = useState({
    productId: '',
    buyerPerson: null,
    buyerPersonType: 0,
    quantity: 1,
    paymentMethod: ProductPaymentMethod.Cash,
    paymentDate: '',
  });
  const [buyerQuery, setBuyerQuery] = useState('');
  const [buyerOptions, setBuyerOptions] = useState([]);
  const [buyerLoading, setBuyerLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const selectedProduct = activeProducts.find((p) => p.id === form.productId);

  useEffect(() => {
    if (!open) return;
    setError('');
    setSubmitting(false);
    setForm({
      productId: activeProducts[0]?.id || '',
      buyerPerson: null,
      buyerPersonType: 0,
      quantity: 1,
      paymentMethod: ProductPaymentMethod.Cash,
      paymentDate: '',
    });
    setBuyerQuery('');
    setBuyerOptions([]);
  }, [open, activeProducts]);

  useEffect(() => {
    if (!open || !buyerQuery || buyerQuery.trim().length < 2) {
      setBuyerOptions([]);
      return;
    }
    let active = true;
    setBuyerLoading(true);
    searchMembersWithSubMembers(buyerQuery.trim())
      .then((data) => {
        if (!active) return;
        const flattened = [];
        (Array.isArray(data) ? data : []).forEach((item) => {
          if (!item) return;
          flattened.push({
            id: item.id,
            fullName: item.fullName,
            profilePictureUrl: item.profilePictureUrl,
            type: 0,
          });
          (item.subMembers || []).forEach((s) => {
            if (!s) return;
            flattened.push({
              id: s.id,
              fullName: s.fullName,
              profilePictureUrl: s.profilePictureUrl,
              type: 1,
            });
          });
        });
        setBuyerOptions(flattened);
      })
      .finally(() => { if (active) setBuyerLoading(false); });
    return () => { active = false; };
  }, [buyerQuery, open]);

  const estimatedTotal = selectedProduct && form.quantity
    ? Number(selectedProduct.price) * Number(form.quantity)
    : null;

  const handleSubmit = async () => {
    if (!form.productId) {
      setError('Ürün seçiniz');
      return;
    }
    if (!form.buyerPerson?.id) {
      setError('Alıcı seçiniz');
      return;
    }
    if (!form.quantity || form.quantity < 1) {
      setError('Geçerli adet giriniz');
      return;
    }
    if (selectedProduct?.maxQuantityPerOrder && form.quantity > selectedProduct.maxQuantityPerOrder) {
      setError(`Sipariş başına en fazla ${selectedProduct.maxQuantityPerOrder} adet`);
      return;
    }
    if (selectedProduct?.stockQuantity != null && form.quantity > selectedProduct.stockQuantity) {
      setError('Yetersiz stok');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await createProductOrder({
        productId: form.productId,
        buyerPersonId: form.buyerPerson.id,
        buyerPersonType: form.buyerPerson.type ?? 0,
        quantity: Number(form.quantity),
        paymentMethod: form.paymentMethod,
        paymentDate: form.paymentDate ? new Date(form.paymentDate).toISOString() : null,
      });
      toast.success('Manuel satış oluşturuldu ve ödendi olarak işaretlendi');
      onClose();
      if (onCreated) onCreated();
    } catch (e) {
      setError(e?.message || 'Satış oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '18px', fontWeight: 500, display: 'flex', justifyContent: 'space-between', pr: 3 }}>
        Manuel Ürün Satışı
        <IconButton onClick={onClose} size="small" disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity="info" sx={{ fontSize: '13px' }}>
            Manuel satışlar Logo&apos;ya otomatik aktarılmaz. Tutar backend tarafından fiyat × adet ile hesaplanır. Havale kabul edilmez.
          </Alert>
          <TextField
            select
            label="Ürün"
            value={form.productId}
            onChange={(e) => setForm((p) => ({ ...p, productId: e.target.value }))}
            fullWidth
            sx={fieldSx}
          >
            {activeProducts.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
                {p.stockQuantity != null ? ` (stok: ${p.stockQuantity})` : ' (sınırsız)'}
              </MenuItem>
            ))}
          </TextField>
          {selectedProduct && (
            <Typography sx={{ fontSize: '13px', color: '#666' }}>
              Birim fiyat: {Number(selectedProduct.price).toLocaleString('tr-TR')} {getCurrencyDisplayLabel(selectedProduct.currency)}
              {estimatedTotal != null && ` · Tahmini toplam: ${estimatedTotal.toLocaleString('tr-TR')} ${getCurrencyDisplayLabel(selectedProduct.currency)}`}
            </Typography>
          )}
          <Autocomplete
            options={buyerOptions}
            loading={buyerLoading}
            value={form.buyerPerson}
            onChange={(_, v) => setForm((p) => ({
              ...p,
              buyerPerson: v,
              buyerPersonType: v?.type ?? 0,
            }))}
            onInputChange={(_, v) => setBuyerQuery(v)}
            getOptionLabel={(o) => o?.fullName || ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            renderOption={(props, option) => (
              <Stack component="li" direction="row" spacing={1} alignItems="center" {...props} key={option.id}>
                <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 28, height: 28 }} />
                <Typography variant="body2">{option.fullName}</Typography>
                <Chip size="small" label={option.type === 1 ? 'Yakın' : 'Üye'} sx={{ ml: 'auto' }} />
              </Stack>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Alıcı" placeholder="Ara..." sx={fieldSx} />
            )}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Adet"
                type="number"
                inputProps={{ min: 1, max: 9999 }}
                value={form.quantity}
                onChange={(e) => setForm((p) => ({ ...p, quantity: Number(e.target.value) || 1 }))}
                fullWidth
                sx={fieldSx}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Ödeme Yöntemi"
                value={form.paymentMethod}
                onChange={(e) => setForm((p) => ({ ...p, paymentMethod: Number(e.target.value) }))}
                fullWidth
                sx={fieldSx}
              >
                <MenuItem value={0}>Nakit</MenuItem>
                <MenuItem value={2}>Kredi Kartı</MenuItem>
                <MenuItem value={3}>Online Ödeme</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Ödeme Tarihi"
                type="datetime-local"
                value={form.paymentDate}
                onChange={(e) => setForm((p) => ({ ...p, paymentDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>İptal</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || activeProducts.length === 0}
          sx={{ textTransform: 'none', backgroundColor: '#0E4D94' }}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProductOrderDialog;
